/**
 * Set a new password — where a recovery link lands (#21, item J).
 *
 * **The one page in this SPA that is meant to be opened in an ordinary
 * browser.** Everything else needs the desktop bridge and says so; a reset link
 * arrives in an email client and is followed in whatever browser the machine
 * defaults to, so a page that answered "open me in the app" would be a dead end
 * at exactly the moment the user cannot get into the app. `App.tsx` lets this
 * route — and only this route — past that gate.
 *
 * Deliberately **not** a deep link into the desktop (owner, 2026-09-04). A
 * custom protocol handoff was the other half of #21's evaluation; the decision
 * is a plain web landing, which needs no installed app, no protocol
 * registration, and no second story for the user who has neither.
 *
 * **How the session gets here.** GoTrue puts the recovery token in the URL
 * *fragment* (`#access_token=…&type=recovery`), and `supabaseClient` is built
 * with `detectSessionInUrl: true`, so the client consumes it on load and leaves
 * a short-lived session behind — which is what makes `updateUser` legal without
 * anyone signing in. That is also why the route table is path-based: a hash
 * router would be parsing the bytes GoTrue is using as a payload (`routes.ts`).
 *
 * The fragment never reaches a server and is cleared once consumed, so the token
 * stays out of request logs and out of the address bar.
 *
 * **Success does not sign anyone in here.** The shell needs the local agent, and
 * this is a browser — so the recovery session is ended and the page finishes on
 * a confirmation, exactly as `bimgo-ai-web`'s own `UpdatePassword` does: the
 * account is ready, and a first-time user is pointed at the install docs.
 *
 * **Nothing here detects whether the desktop app is installed** (owner,
 * 2026-09-04). #21 raised detection and an installer hand-off as options; the
 * decision is the web client's approach — one static documentation link, offered
 * to everyone. A browser cannot see what is installed without a protocol probe,
 * and a probe that guesses wrong sends a user who already has the app to a
 * download page, or hides the link from the one person who needs it.
 */
import { useEffect, useState, type FormEvent } from "react";
import { useLocale } from "../i18n/LocaleProvider";
import { supabase } from "../lib/supabaseClient";
import "../ui/brand.css";

/** GoTrue's own minimum. Stated here so the field can say so before submitting. */
const MIN_LENGTH = 6;

/**
 * Where a first-time user goes to install the desktop app.
 *
 * A **static** link to the 2.0 installation guide (owner, 2026-10-18): the page
 * that walks a new user through the desktop installer and then the Revit /
 * AutoCAD plugins. It is the one thing on this screen nobody can correct from
 * inside the app, so a docs reshuffle that moves it has to update this line.
 * `bimgo-ai-web` ships the same idea with its own product's page.
 */
const INSTALL_DOCS_URL = "https://bimgo-ai.gitbook.io/docs/2.0/installation";
/**
 * The desktop's custom scheme, registered per user by the app itself at every
 * startup (`app.setAsDefaultProtocolClient` in main.ts — on Windows that is the
 * registration; electron-builder's `protocols` is macOS-only). Inert: `open` and
 * nothing else, ever — recovery tokens do not travel in URLs (#21), and this
 * button does not need one.
 */
const OPEN_DESKTOP_URL = "bimgo://open";

type Recovery =
  | { kind: "checking" }
  | { kind: "ready" }
  /** No token, or one GoTrue would not take — expired, used, or hand-edited. */
  | { kind: "invalid" };

export default function UpdatePassword({ previewDone = false }: { previewDone?: boolean } = {}) {
  const { t } = useLocale();

  const [recovery, setRecovery] = useState<Recovery>({ kind: "checking" });
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // `previewDone` is the dev-only `?preview=password-done` (App.tsx): the
  // confirmation is otherwise reachable only by spending a real recovery link.
  const [done, setDone] = useState(previewDone);

  /**
   * Is there a recovery session to spend?
   *
   * `detectSessionInUrl` consumes the fragment asynchronously, so asking
   * `getSession()` on the first render can race it and answer "no" for a link
   * that is perfectly good. `onAuthStateChange` fires `PASSWORD_RECOVERY` when
   * the token lands, so both are watched: the listener for the normal case, one
   * `getSession` for the case where it was already consumed before this mounted.
   */
  useEffect(() => {
    let cancelled = false;
    const client = supabase();

    const { data: subscription } = client.auth.onAuthStateChange((event, session) => {
      if (cancelled) return;
      if (event === "PASSWORD_RECOVERY" || session) setRecovery({ kind: "ready" });
    });

    client.auth
      .getSession()
      .then(({ data }) => {
        if (cancelled) return;
        // Only downgrade to "invalid" from "checking" — a listener that already
        // reported a session must not be overruled by a slower lookup.
        setRecovery((was) =>
          data.session ? { kind: "ready" } : was.kind === "checking" ? { kind: "invalid" } : was,
        );
      })
      .catch(() => {
        if (!cancelled) setRecovery({ kind: "invalid" });
      });

    return () => {
      cancelled = true;
      subscription.subscription.unsubscribe();
    };
  }, []);

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    // Checked here rather than left to GoTrue: a round trip to be told the two
    // boxes differ is a round trip the browser did not need to make.
    if (password !== confirm) {
      setError(t("updatePassword.mismatch"));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const { error: updateError } = await supabase().auth.updateUser({ password });
      if (updateError) {
        setError(updateError.message);
        return;
      }
      // The recovery session has done its one job. Ending it means a shared or
      // borrowed machine is not left holding a usable session for this account.
      //
      // **A failed sign-out is reported, not swallowed** — the password *was*
      // changed, so silently continuing would leave a live session behind a
      // success message. `scope: "local"` because only this browser's session is
      // ours to end; the desktop's is not.
      const { error: signOutError } = await supabase().auth.signOut({ scope: "local" });
      if (signOutError) {
        setError(t("updatePassword.signOutError"));
        return;
      }
      setDone(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="auth-page">
      <div className="auth-shell">
        <div className="auth-header">
          <img className="auth-logo" src="/bimgo-logo-horizontal-color.png" alt="Bimgo AI" />
        </div>

        <div className="auth-card">
          {done ? (
            <div className="auth-done">
              <h1 className="auth-title">{t("updatePassword.accountReadyTitle")}</h1>
              <p className="auth-message is-ok">{t("updatePassword.accountReadyDesc")}</p>
              {/* The way back into the desktop app (#72): a plain `bimgo://open`
                  link, which Windows hands to the app if one is installed. No
                  probing for the app and no branching on the answer — a browser
                  cannot ask, and the timer heuristic misfires on Chrome's "Open
                  Bimgo AI 2.0?" dialog — so this and the installation link
                  below are both always shown. The URL carries nothing: the user
                  signs in with the password they have just set. */}
              <a className="auth-button is-link" href={OPEN_DESKTOP_URL}>
                {t("updatePassword.openDesktop")}
              </a>
              <p className="auth-subtitle">{t("updatePassword.doneReturnToApp")}</p>

              <hr className="auth-sep" />

              <h2 className="auth-title">{t("updatePassword.newUserTitle")}</h2>
              <p className="auth-subtitle">{t("updatePassword.newUserDesc")}</p>
              {/* `noreferrer` as well as `noopener`: this page has a recovery
                  token in its history, and the docs site has no business
                  learning where the reader came from. */}
              <a
                className="auth-button is-link is-secondary"
                href={INSTALL_DOCS_URL}
                target="_blank"
                rel="noopener noreferrer"
              >
                {t("updatePassword.installDesktop")}
              </a>
            </div>
          ) : recovery.kind === "checking" ? (
            <p className="auth-subtitle">{t("updatePassword.checking")}</p>
          ) : recovery.kind === "invalid" ? (
            <div>
              <h1 className="auth-title">{t("updatePassword.invalidTitle")}</h1>
              <p className="auth-message is-error" role="alert">
                {t("updatePassword.invalidDesc")}
              </p>
              <p className="auth-subtitle">{t("updatePassword.invalidRetry")}</p>
            </div>
          ) : (
            <form onSubmit={onSubmit}>
              <h1 className="auth-title">{t("updatePassword.title")}</h1>
              <p className="auth-subtitle">{t("updatePassword.subtitle")}</p>

              <div className="auth-field">
                <label className="auth-label" htmlFor="new-password">
                  {t("updatePassword.newLabel")}
                </label>
                <input
                  id="new-password"
                  className="auth-input"
                  type="password"
                  autoComplete="new-password"
                  placeholder={t("updatePassword.placeholder")}
                  minLength={MIN_LENGTH}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </div>

              <div className="auth-field">
                <label className="auth-label" htmlFor="confirm-password">
                  {t("updatePassword.confirmLabel")}
                </label>
                <input
                  id="confirm-password"
                  className="auth-input"
                  type="password"
                  autoComplete="new-password"
                  placeholder={t("updatePassword.confirmPlaceholder")}
                  minLength={MIN_LENGTH}
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  required
                />
              </div>

              {error ? (
                <p className="auth-message is-error" role="alert">
                  {error}
                </p>
              ) : null}

              <div style={{ marginTop: "1rem" }}>
                <button
                  type="submit"
                  className="auth-button"
                  disabled={busy || !password || !confirm}
                >
                  {busy ? t("updatePassword.updating") : t("updatePassword.confirm")}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </main>
  );
}
